import React, { useState, useEffect } from 'react';

interface AddMilestoneModalProps {
  goalId: string;
  goalTitle: string;
  goalStartDate?: string;
  goalEndDate?: string;
  onSave: (data: { title: string; targetDate: string; globalPeriod?: string }) => void;
  onCancel: () => void;
}

/**
 * AddMilestoneModal - Modal voor handmatig toevoegen van mijlpalen
 * 
 * Features:
 * - Titel input
 * - Datum picker (beperkt tot doel datumbereik)
 * - Optionele periode label (bijv. "Q2 2026", "Half jaar")
 * - Suggesties voor gangbare intervallen
 */
const AddMilestoneModal: React.FC<AddMilestoneModalProps> = ({
  goalId,
  goalTitle,
  goalStartDate,
  goalEndDate,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [globalPeriod, setGlobalPeriod] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Bereken suggesties gebaseerd op doelduur
  const suggestions = React.useMemo(() => {
    if (!goalStartDate || !goalEndDate) return [];

    const start = new Date(goalStartDate);
    const end = new Date(goalEndDate);
    const durationMs = end.getTime() - start.getTime();
    const durationDays = durationMs / (1000 * 60 * 60 * 24);
    const durationMonths = durationDays / 30;
    const durationYears = durationDays / 365;

    const result: { label: string; date: string; period: string }[] = [];

    // Genereer suggesties afhankelijk van doelduur
    if (durationYears >= 2) {
      // Lange termijn (2+ jaar): jaarlijkse mijlpalen
      for (let i = 1; i <= Math.min(Math.floor(durationYears), 10); i++) {
        const date = new Date(start);
        date.setFullYear(date.getFullYear() + i);
        if (date <= end) {
          result.push({
            label: `Over ${i} jaar`,
            date: date.toISOString().split('T')[0],
            period: `Jaar ${i}`,
          });
        }
      }
    } else if (durationMonths >= 6) {
      // Middellange termijn (6-24 maanden): kwartaal mijlpalen
      const quarters = Math.min(Math.floor(durationMonths / 3), 8);
      for (let i = 1; i <= quarters; i++) {
        const date = new Date(start);
        date.setMonth(date.getMonth() + i * 3);
        if (date <= end) {
          result.push({
            label: `Over ${i * 3} maanden`,
            date: date.toISOString().split('T')[0],
            period: `Q${i}`,
          });
        }
      }
    } else if (durationMonths >= 1) {
      // Korte termijn (1-6 maanden): maandelijkse mijlpalen
      const months = Math.min(Math.floor(durationMonths), 6);
      for (let i = 1; i <= months; i++) {
        const date = new Date(start);
        date.setMonth(date.getMonth() + i);
        if (date <= end) {
          result.push({
            label: `Over ${i} maand${i > 1 ? 'en' : ''}`,
            date: date.toISOString().split('T')[0],
            period: `Maand ${i}`,
          });
        }
      }
    } else {
      // Zeer kort (< 1 maand): wekelijkse mijlpalen
      const weeks = Math.min(Math.floor(durationDays / 7), 4);
      for (let i = 1; i <= weeks; i++) {
        const date = new Date(start);
        date.setDate(date.getDate() + i * 7);
        if (date <= end) {
          result.push({
            label: `Over ${i} week${i > 1 ? 'weken' : ''}`,
            date: date.toISOString().split('T')[0],
            period: `Week ${i}`,
          });
        }
      }
    }

    // Voeg altijd "halverwege" en "einddoel" toe als opties
    const halfwayDate = new Date(start.getTime() + durationMs / 2);
    result.unshift({
      label: 'Halverwege',
      date: halfwayDate.toISOString().split('T')[0],
      period: 'Halverwege',
    });

    return result.slice(0, 6); // Max 6 suggesties
  }, [goalStartDate, goalEndDate]);

  const handleSuggestionClick = (suggestion: { label: string; date: string; period: string }) => {
    setTargetDate(suggestion.date);
    setGlobalPeriod(suggestion.period);
    setShowSuggestions(false);
  };

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      targetDate,
      globalPeriod: globalPeriod.trim() || undefined,
    });
  };

  const isValid = title.trim() !== '';

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90dvh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Nieuwe mijlpaal toevoegen
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                voor: {goalTitle}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Titel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Wat wil je bereiken? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="bijv. Eerste 10 klanten, Website live, MVP getest..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Suggesties */}
          {showSuggestions && suggestions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kies een moment:
              </label>
              <div className="grid grid-cols-2 gap-2">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="px-3 py-2 text-sm text-left bg-gray-50 hover:bg-orange-50 hover:text-orange-700 border border-gray-200 hover:border-orange-300 rounded-lg transition-colors"
                  >
                    <span className="font-medium">{suggestion.label}</span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      {formatDate(suggestion.date)}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowSuggestions(false)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700"
              >
                Of kies zelf een datum →
              </button>
            </div>
          )}

          {/* Datum picker (altijd zichtbaar als suggesties verborgen) */}
          {!showSuggestions && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Streefdatum
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                min={goalStartDate || new Date().toISOString().split('T')[0]}
                // Allow dates up to goal end date OR 10 years from now, whichever is later
                max={(() => {
                  const tenYearsFromNow = new Date();
                  tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);
                  const maxDate = tenYearsFromNow.toISOString().split('T')[0];
                  if (!goalEndDate) return maxDate;
                  return goalEndDate > maxDate ? goalEndDate : maxDate;
                })()}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">
                Kies een datum vanaf {formatDate(goalStartDate || new Date().toISOString().split('T')[0])}
              </p>
              {suggestions.length > 0 && (
                <button
                  onClick={() => setShowSuggestions(true)}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  ← Terug naar suggesties
                </button>
              )}
            </div>
          )}

          {/* Periode label (optioneel) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Periode label <span className="text-gray-400 text-xs">(optioneel)</span>
            </label>
            <input
              type="text"
              value={globalPeriod}
              onChange={(e) => setGlobalPeriod(e.target.value)}
              placeholder="bijv. Q2 2026, Half jaar, Jaar 1..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">
              Dit label wordt getoond op de tijdlijn
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-5 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Mijlpaal toevoegen
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMilestoneModal;
